import { type TypedDocumentNode, gql } from '@apollo/client';
import { faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import {
  BenchPlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { glossary } from '@sorare/core/src/lib/glossary';

import { CardFooter } from 'components/card/CardFooter';
import { useIsBoostAffordable } from 'hooks/useIsBoostAffordable';

import type { TokenBoostDetails_anyCard } from './__generated__/index.graphql';

type Props = {
  anyCard: TokenBoostDetails_anyCard;
  onInfoClick?: () => void;
  totalXpNeeded?: number;
  onToggleCard?: (slug: string, options?: { xpNeeded?: number }) => void;
  selected?: boolean;
};

export const TokenBoostDetails = ({
  anyCard,
  onInfoClick,
  onToggleCard,
  totalXpNeeded = 0,
  selected,
}: Props) => {
  const { sport } = useSportContext();
  const {
    flags: { useMlbSeasonAverageScore = true },
  } = useFeatureFlags();

  const isBoostAffordable = useIsBoostAffordable();

  const modePerSport: Record<Sport, BenchPlayerScore> = {
    [Sport.FOOTBALL]: BenchPlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
    [Sport.NBA]: BenchPlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [Sport.BASEBALL]: useMlbSeasonAverageScore
      ? BenchPlayerScore.SEASON_AVERAGE_SCORE
      : BenchPlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
  };
  const mode = modePerSport[sport];

  const { affordable, xpNeeded } = isBoostAffordable(anyCard, totalXpNeeded);
  const isMaxLevel = anyCard.xpNeededForNextGrade === null;

  const disabled = !selected && (!affordable || isMaxLevel);

  let icon: ReactNode = null;
  if (selected) {
    icon = <FontAwesomeIcon icon={faRotateLeft} />;
  } else if (!isMaxLevel) {
    icon = <Boost size={1.5} rarity={disabled ? undefined : anyCard.rarity} />;
  }

  return (
    <Vertical gap={1.5}>
      <CardFooter
        anyCard={anyCard}
        benchPlayerScore={mode}
        onClick={onInfoClick}
      />
      <Button
        size="small"
        color="quaternary"
        startIcon={icon}
        disabled={disabled}
        onClick={() =>
          onToggleCard?.(anyCard.slug, {
            xpNeeded,
          })
        }
      >
        {selected ? (
          <FormattedMessage
            id="TokenBoostDetails.undoBoost"
            defaultMessage="Undo Boost"
          />
        ) : (
          <>
            {isMaxLevel ? (
              <FormattedMessage {...glossary.max} />
            ) : (
              <FormattedNumber value={xpNeeded} />
            )}
          </>
        )}
      </Button>
    </Vertical>
  );
};

TokenBoostDetails.fragments = {
  anyCard: gql`
    fragment TokenBoostDetails_anyCard on AnyCardInterface {
      slug
      rarity: rarityTyped
      xpNeededForNextGrade
      tokenOwner {
        id
        transferType
      }
      ...CardFooter_anyCardInterface
      ...useIsBoostAffordable_anyCard
    }
    ${CardFooter.fragments.anyCardInterface}
    ${useIsBoostAffordable.fragments.anyCard}
  ` as TypedDocumentNode<TokenBoostDetails_anyCard>,
};
